import { runWithRetry } from '@yi/core/src/runWithRetry';

import { utilities } from '~/src/common';
import { nr } from '~/src/common/newRelic';
import { loadScriptOnce } from '~/src/common/utilities/loadScriptOnce';

import { waitFor } from './utilities/waitFor';

const { isSSR } = utilities;

// Types not clearly documented at time of implementation, augment as needed
type RuntimeAction = any;
interface LoadConfig {
  verify: { projectID: string };
  url: string;
  versionID: 'production' | 'development';
  voice: { url: string };
  launch?: {
    event: {
      type: 'launch';
      payload: {
        test: true;
        // Example
        // user_name: 'Mary';
        // user_email: 'mary@test.com';
      };
    };
  };
}

interface VoiceflowChat {
  load: (config: LoadConfig) => Promise<void>;
  destroy: () => void;

  interact: (action: RuntimeAction) => Promise<void>;

  open: VoidFunction;
  close: VoidFunction;
  hide: VoidFunction;
  show: VoidFunction;

  proactive: {
    clear: () => void;
    push: (...messages: { type: 'text'; payload: { message: string } }[]) => void;
  };
}

declare global {
  interface Window {
    voiceflow?: {
      chat?: VoiceflowChat;
    };
  }
}

async function getVoiceflow() {
  if (isSSR()) return null;

  try {
    return await runWithRetry(
      async () => {
        if (!window.voiceflow?.chat) throw new Error('Failed to get window.voiceflow.chat');
        return window.voiceflow.chat;
      },
      { maxRetries: 4 },
    );
  } catch (e) {
    nr.noticeError(`Failed to get Voiceflow: ${e}`);
    throw e;
  }
}

async function ensureScriptTagLoaded() {
  await loadScriptOnce({
    src: `https://cdn.voiceflow.com/widget-next/bundle.mjs`,
    id: 'voiceflow-snippet',
  });
}

let loadedProjectPromise: Promise<void> | null = null;
async function ensureProjectLoaded(projectID: string) {
  if (loadedProjectPromise) return loadedProjectPromise;

  loadedProjectPromise = (async () => {
    const vf = await getVoiceflow();
    if (!vf) throw new Error(`Could not ensure voiceflow project loaded`);

    vf.load({
      verify: { projectID },
      url: 'https://general-runtime.voiceflow.com',
      versionID: 'production',
      voice: {
        url: 'https://runtime-api.voiceflow.com',
      },
    });
    // Sometimes API calls warn that .load() was not called when it was. Could not find any
    // Voiceflow API or side effect to ensure fully loaded, but this seems to help :/
    await waitFor(2000);
  })();

  return loadedProjectPromise;
}

export const voiceflow = {
  PROJECT_ID_LOGGED_IN: '6859bacec2f384a0e1b5b0b2',
  PROJECT_ID_LOGGED_OUT: '686bf9c22f415077a21f0eec',

  ensureLoaded: async (projectID: string) => {
    await ensureScriptTagLoaded();
    await ensureProjectLoaded(projectID);
  },

  wasLoaded: async () => {
    if (!loadedProjectPromise) return false;
    await loadedProjectPromise;
    return true;
  },

  clearHistoryFromLocalStorage: () => {
    // Voiceflow stores history in local storage with keys like: voiceflow-session-686bf9c22f415077a21f0eec
    // Unfortunately web widget does not have an API to clear this history, so we depend on these implementation details
    const VOICEFLOW_KEY_PREFIX = 'voiceflow-';
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(VOICEFLOW_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  },

  // Expand the chat window
  open: () => getVoiceflow().then(vf => vf?.open()),

  // Collapse the chat window
  close: () => getVoiceflow().then(vf => vf?.close()),

  // Completely hide UI
  hide: () => getVoiceflow().then(vf => vf?.hide()),

  // Show the UI (expanded or collapsed depends on previous state)
  show: () => getVoiceflow().then(vf => vf?.show()),

  showProactiveMessages: (messages: string[]) =>
    getVoiceflow().then(vf => {
      vf?.proactive.push(
        ...messages.map(message => ({
          type: 'text' as const,
          payload: { message },
        })),
      );
    }),
};
